// SECURITY: All endpoints MUST use /auth/* paths (relative to /api/admin base).
// NEVER call /api/v1/auth/* — that is the webapp auth domain.

import apiClient from './client'
import type { LoginRequest, LoginResponse, MfaVerifyRequest, TokenPair } from '@/types/auth'
import type { MfaSetupResponse, MfaVerifySetupResponse, RecoveryCodesResponse } from '@/types/mfa'
import type { AdminUser } from '@/types/admin-user'

// ---------------------------------------------------------------------------
// Auth API — admin authentication endpoints
// ---------------------------------------------------------------------------

/**
 * Authenticate admin user with email + password.
 * Returns access_token + refresh_token (or requires_mfa + mfa_token).
 */
export async function postLogin(email: string, password: string): Promise<LoginResponse> {
  const payload: LoginRequest = { email, password }
  const { data } = await apiClient.post<LoginResponse>('/auth/login', payload)
  return data
}

/**
 * Verify MFA code after successful credential login.
 * Returns the full token pair upon successful MFA verification.
 */
export async function postMfaVerify(code: string, mfaSessionToken: string | null): Promise<TokenPair> {
  const payload: MfaVerifyRequest = { code, mfa_token: mfaSessionToken }
  const { data } = await apiClient.post<TokenPair>('/auth/mfa/verify', payload)
  return data
}

/**
 * Logout — invalidates the current session on the backend.
 */
export async function postLogout(): Promise<void> {
  await apiClient.post('/auth/logout')
}

/**
 * Refresh the access token using the HttpOnly refresh cookie.
 * The browser sends the `admin_refresh_token` cookie automatically
 * (withCredentials: true is set on the axios client).
 * No request body needed — the backend reads the refresh token from the cookie.
 */
export async function postRefresh(): Promise<TokenPair> {
  const { data } = await apiClient.post<TokenPair>('/auth/refresh')
  return data
}

/**
 * Initiate MFA setup for an admin user who has not yet enrolled.
 * Returns the TOTP secret and a QR URI for authenticator apps.
 */
export async function postMfaSetup(): Promise<MfaSetupResponse> {
  const { data } = await apiClient.post<MfaSetupResponse>('/auth/mfa/setup')
  return data
}

/**
 * Verify MFA setup by validating the first TOTP code.
 * Returns a message and recovery codes upon successful verification.
 */
export async function postMfaVerifySetup(code: string): Promise<MfaVerifySetupResponse> {
  const { data } = await apiClient.post<MfaVerifySetupResponse>('/auth/mfa/verify-setup', { code })
  return data
}

/**
 * Retrieve MFA recovery codes for the authenticated admin user.
 */
export async function getMfaRecoveryCodes(): Promise<RecoveryCodesResponse> {
  const { data } = await apiClient.get<RecoveryCodesResponse>('/auth/mfa/recovery-codes')
  return data
}

/**
 * Fetch the current authenticated admin user's profile.
 */
export async function getCurrentUser(): Promise<AdminUser> {
  const { data } = await apiClient.get<AdminUser>('/auth/me')
  return data
}
